import React from 'react'
import Head from 'next/head'
import Link from 'next/link'

import BasePage from 'modules/marketplace/layout/components/BasePage'
import ButtonAnchor from 'components/ButtonAnchor'
import { RegularContainer } from 'modules/marketplace/layout/components/ResponsiveContainers'

import styles from '../../styles/integrations.module.css'

const XERO_XTID = 'x30fieldfolio'
const XERO_BRAND_COLOR = 'hsl(195, 85%, 50%)'
const XERO_BRAND_COLOR_DARK = 'hsl(195, 100%, 30%)'

const XeroPage = () => (
  <BasePage hasMarketingFooter>
    <Head>
      <title>Integrate your sales and ordering seamlessly with Xero · Fieldfolio</title>
      <meta name="description" content="Integrate your sales and ordering seamlessly with Xero." />
      <link rel="canonical" href={`${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/integrations/xero`} />
    </Head>

    <RegularContainer>
      <div className={styles.logoHeader}>
        <img
          src="/marketing/integrations/xero/logo-xero-app-partner-connected-badge.svg"
          alt="Xero Connected App Badge"
        />
      </div>

      <div className={styles.pageHeader}>
        <h1 className="headline">Fieldfolio + Xero: Seamless wholesale ordering</h1>
        <h2 className="subhead">Instantly sync wholesale orders and inventory with Xero.</h2>
        <p>
          Sync orders from Fieldfolio for iPad or online to your Xero account, and retrieve stock-on-hand numbers from
          Xero without lifting a finger.
        </p>
        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
          </Link>
          <ButtonAnchor
            className={styles.brandCtaButton}
            target="_blank"
            href={`https://xero.com/signup/?xtid=${XERO_XTID}`}>
            Try Xero for Free
          </ButtonAnchor>
        </div>
      </div>

      <div className={styles.pageSection}>
        <div className={styles.screenshotsContainer}>
          <img src="/marketing/marketplace-screenshots.png" alt="Fieldfolio wholesale sales and ordering client apps" />
          <img src="/marketing/integrations/xero/xero-screenshot.png" alt="Xero web app screenshot" />
        </div>
        <h3>Save time and eliminate data entry errors</h3>
        <h4>Order syncing</h4>
        <p>
          Fieldfolio instantly syncs orders from our iPad app and our online marketplace to your Xero account, saving
          time and eliminating back-office errors.
        </p>
        <h4>Stock-on-hand syncing</h4>
        <p>
          Everytime an order is synced, the stock-on-hand level is retrieved from Xero and updated in Fieldfolio to
          ensure you never sell more than you have in-stock.
        </p>
        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
          </Link>
        </div>
      </div>
    </RegularContainer>

    <div
      className={styles.aboutSection}
      style={{
        color: 'white',
        background: 'linear-gradient(hsl(250,80%,60%), hsl(260,70%,45%))'
      }}>
      <RegularContainer>
        <h2>Fieldfolio</h2>
        <h3>Australia&apos;s No.1 wholesale eCommerce platform.</h3>
        <h4>Online Marketplace with thousand of verified retail buyers</h4>
        <p>You bring the product catalog, we bring the buyers.</p>
        <h4>iPad sales and ordering app for Reps</h4>
        <p>Intuitive catalog display and ordering for iPad. Tradeshow friendly and full offline support.</p>

        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="reverse">Sell on Fieldfolio</ButtonAnchor>
          </Link>
        </div>
      </RegularContainer>
    </div>

    <div
      className={styles.aboutSection}
      style={{
        color: 'white',
        background: `linear-gradient(${XERO_BRAND_COLOR}, ${XERO_BRAND_COLOR_DARK})`
      }}>
      <RegularContainer>
        <h2>Xero</h2>
        <h3>World-leading online accounting software built for small business.</h3>
        <div className={styles.featuresPanel}>
          <div>
            <h4>See your up-to-date cash position</h4>
            <p>
              Know how you’re doing financially with secure daily updates from your bank accounts and a clear visual
              dashboard.
            </p>
            <h4>Get paid faster and improve cash flow</h4>
            <p>
              Easily create and send invoices with online payments and automated invoice reminders for faster payment.
            </p>
            <h4>Run your business from anywhere</h4>
            <p>
              Access your business finances anytime, from any internet-connected device, and use the Xero app for iOS
              and Android.
            </p>
            <h4>Connect and collaborate anytime</h4>
            <p>
              Invite your team and your advisors to work with you in real time from home, office, or on the go using the
              mobile app.
            </p>
            <div className={styles.ctaGroup}>
              <ButtonAnchor kind="reverse" target="_blank" href={`https://xero.com/signup/?xtid=${XERO_XTID}`}>
                Try Xero for Free
              </ButtonAnchor>
            </div>
          </div>
          <div>
            <img src="/marketing/integrations/xero/xero-about-diagram.png" alt="Xero feature cards" />
          </div>
        </div>
      </RegularContainer>
    </div>

    <div className={styles.pageSection}>
      <RegularContainer>
        <h2>Connecting Fieldfolio to Xero is easy</h2>

        <h4>Getting started tutorial</h4>
        <ul className={styles.helpLinkList}>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201248-setup-the-xero-integration`}>
              Setup the Xero Integration
            </a>
          </li>
        </ul>

        <h4>Documentation</h4>
        <ul className={styles.helpLinkList}>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201258-xero-integration`}>
              Xero Integration
            </a>
          </li>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201261-xero-integration-settings`}>
              Xero Integration Settings
            </a>
          </li>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/articles/6201266-xero-integration-log`}>
              Xero Integration Log
            </a>
          </li>
        </ul>
      </RegularContainer>
    </div>

    <div className={styles.pageSection}>
      <RegularContainer>
        <h3>Read our help docs</h3>
        <ButtonAnchor
          kind="primary"
          icon="external-link"
          iconPosition="right"
          target="_blank"
          href={`${process.env.NEXT_PUBLIC_HELP_URL}/en/collections/3413401-wholesalers`}>
          Wholesaler Help
        </ButtonAnchor>
      </RegularContainer>
    </div>

    <footer className={styles.footer}>
      <RegularContainer>
        <h2>Signup and get started today</h2>
        <div className={styles.ctaGroup}>
          <Link href="/signup/wholesaler" passHref>
            <ButtonAnchor kind="primary">Sell on Fieldfolio</ButtonAnchor>
          </Link>
          <ButtonAnchor
            className={styles.brandCtaButton}
            target="_blank"
            href={`https://xero.com/signup/?xtid=${XERO_XTID}`}>
            Try Xero for Free
          </ButtonAnchor>
        </div>
      </RegularContainer>
    </footer>
  </BasePage>
)

export default XeroPage
